import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { type RouteObject } from 'react-router-dom';

import { AppShell } from '@/shared/components/Layout/AppShell/AppShell';
import { PermissionRoute } from '@/shared/components/Layout/PermissionRoute/PermissionRoute';
import { ProtectedRoute } from '@/shared/components/Layout/ProtectedRoute/ProtectedRoute';
import { FormSkeleton } from '@/shared/components/feedback/Skeleton/Skeleton';

import { PRIVATE_ROUTES, PUBLIC_ROUTES } from './route.constant';

/**
 * Route-level code splitting (W1): each module's entry file is its own chunk, so
 * an operator who only ever opens the dashboard never downloads the role editor.
 *
 * `src/routes/*` are the only module-importing files besides the two
 * `*.config` bridges, which is why the `lazy()` calls point at them rather than
 * at deep module paths.
 */
const LoginRoute = lazy(() => import('@/routes/Login'));
const DashboardRoute = lazy(() => import('@/routes/Dashboard'));
const AdminsOverviewRoute = lazy(() => import('@/routes/AdminsOverview'));
const MembersRoute = lazy(() => import('@/routes/Members'));
const MemberDetailRoute = lazy(() => import('@/routes/MemberDetail'));
const StaffUsersRoute = lazy(() => import('@/routes/StaffUsers'));
const StaffUserDetailRoute = lazy(() => import('@/routes/StaffUserDetail'));
const RolesRoute = lazy(() => import('@/routes/Roles'));
const SettingsRoute = lazy(() => import('@/routes/Settings'));
const IntegrationsRoute = lazy(() => import('@/routes/Integrations'));
const QuestionsRoute = lazy(() => import('@/routes/Questions'));
const QuestionEditorRoute = lazy(() => import('@/routes/QuestionEditor'));
const LegalRoute = lazy(() => import('@/routes/Legal'));
const LegalDocumentDetailRoute = lazy(() => import('@/routes/LegalDocumentDetail'));
const LegalVersionEditorRoute = lazy(() => import('@/routes/LegalVersionEditor'));
const LegalVersionConsentsRoute = lazy(() => import('@/routes/LegalVersionConsents'));
const TrustQueueRoute = lazy(() => import('@/routes/TrustQueue'));
const TrustPolicyRoute = lazy(() => import('@/routes/TrustPolicy'));
const VerificationQueueRoute = lazy(() => import('@/routes/VerificationQueue'));
const VerificationCaseRoute = lazy(() => import('@/routes/VerificationCase'));
const InterviewAvailabilityRoute = lazy(() => import('@/routes/InterviewAvailability'));
const ModerationQueueRoute = lazy(() => import('@/routes/ModerationQueue'));
const ViolationQueueRoute = lazy(() => import('@/routes/ViolationQueue'));
const ViolationTypesRoute = lazy(() => import('@/routes/ViolationTypes'));
const NotificationTemplatesRoute = lazy(() => import('@/routes/NotificationTemplates'));
const NotificationTemplateEditorRoute = lazy(() => import('@/routes/NotificationTemplateEditor'));
const NotificationDeliveriesRoute = lazy(() => import('@/routes/NotificationDeliveries'));
const NotificationCampaignsRoute = lazy(() => import('@/routes/NotificationCampaigns'));
const NotificationCampaignComposerRoute = lazy(
  () => import('@/routes/NotificationCampaignComposer'),
);
const NotificationDefaultsRoute = lazy(() => import('@/routes/NotificationDefaults'));
const CoursesRoute = lazy(() => import('@/routes/Courses'));
const MeetingsRoute = lazy(() => import('@/routes/Meetings'));
const BookingDetailRoute = lazy(() => import('@/routes/BookingDetail'));
const VenuesRoute = lazy(() => import('@/routes/Venues'));
const VenueDetailRoute = lazy(() => import('@/routes/VenueDetail'));
const VenueAvailabilityRoute = lazy(() => import('@/routes/VenueAvailability'));
const SubscriptionsRoute = lazy(() => import('@/routes/Subscriptions'));
const SubscriptionDetailRoute = lazy(() => import('@/routes/SubscriptionDetail'));
const SubscriptionPricingRoute = lazy(() => import('@/routes/SubscriptionPricing'));
const OfferCodesRoute = lazy(() => import('@/routes/OfferCodes'));
const PaymentsRoute = lazy(() => import('@/routes/Payments'));
const PaymentEntryRoute = lazy(() => import('@/routes/PaymentEntry'));
const PaymentDetailRoute = lazy(() => import('@/routes/PaymentDetail'));
const BillingEventsRoute = lazy(() => import('@/routes/BillingEvents'));
const EntitlementsRoute = lazy(() => import('@/routes/Entitlements'));
const LedgerRoute = lazy(() => import('@/routes/Ledger'));
const CourseDetailRoute = lazy(() => import('@/routes/CourseDetail'));
const TestsRoute = lazy(() => import('@/routes/Tests'));
const TestDetailRoute = lazy(() => import('@/routes/TestDetail'));
const DiscountRulesRoute = lazy(() => import('@/routes/DiscountRules'));
const DiscountRuleDetailRoute = lazy(() => import('@/routes/DiscountRuleDetail'));
const MemberDiscountsRoute = lazy(() => import('@/routes/MemberDiscounts'));

/**
 * The development-only Overlay Lab (P10.1).
 *
 * The `lazy()` call sits INSIDE the flag rather than beside the others above.
 * `import.meta.env.DEV` is replaced with a literal at build time, so a production
 * build folds this to `null`, the dynamic import becomes unreachable, and the
 * chunk is never emitted. Declared at module scope like every other route
 * component so React sees a stable identity — a `lazy()` evaluated during render
 * remounts its subtree on every pass.
 *
 * No `PermissionRoute`: there is nothing to authorize. The page reads nothing and
 * writes nothing, and every dialog on it is filled with generated filler.
 */
const KitLabRoute = import.meta.env.DEV ? lazy(() => import('@/routes/KitLab')) : null;
const SelectionLabRoute = import.meta.env.DEV ? lazy(() => import('@/routes/SelectionLab')) : null;
const StructuredLabRoute = import.meta.env.DEV
  ? lazy(() => import('@/routes/StructuredLab'))
  : null;
const ScalarLabRoute = import.meta.env.DEV ? lazy(() => import('@/routes/ScalarLab')) : null;
const FormLabRoute = import.meta.env.DEV ? lazy(() => import('@/routes/FormLab')) : null;
const MediaLabRoute = import.meta.env.DEV ? lazy(() => import('@/routes/MediaLab')) : null;
const CalendarLabRoute = import.meta.env.DEV ? lazy(() => import('@/routes/CalendarLab')) : null;

/**
 * The more specific paths FIRST: react-router ranks static segments above dynamic
 * ones, but two literal paths where one is a prefix of the other are only
 * unambiguous because they are different strings — listing the children first
 * keeps that obvious to the next reader.
 *
 * A list rather than a chain of `=== null` guards: with four labs that chain had
 * become four terms nobody would read, and a fifth would be added to the wrong
 * one. There are six now, which is the argument having been right.
 */
const LAB_ROUTES: readonly (readonly [string, ComponentType | null])[] = [
  [PRIVATE_ROUTES.SELECTION_LAB, SelectionLabRoute],
  [PRIVATE_ROUTES.STRUCTURED_LAB, StructuredLabRoute],
  [PRIVATE_ROUTES.SCALAR_LAB, ScalarLabRoute],
  [PRIVATE_ROUTES.FORM_LAB, FormLabRoute],
  [PRIVATE_ROUTES.MEDIA_LAB, MediaLabRoute],
  [PRIVATE_ROUTES.CALENDAR_LAB, CalendarLabRoute],
  [PRIVATE_ROUTES.KIT_LAB, KitLabRoute],
];

const devRoutes: RouteObject[] = LAB_ROUTES.flatMap(([path, Lab]) =>
  Lab === null ? [] : [{ path, element: withSuspense(<Lab />) }],
);

/**
 * A content-shaped placeholder while a chunk downloads.
 *
 * Not a spinner: on a slow connection the fallback is on screen long enough that
 * a shape which matches what arrives keeps the page from jumping.
 */
function withSuspense(element: ReactNode): ReactNode {
  return <Suspense fallback={<FormSkeleton fields={3} />}>{element}</Suspense>;
}

export const publicRoutes: RouteObject[] = [
  { path: PUBLIC_ROUTES.LOGIN, element: withSuspense(<LoginRoute />) },
];

/**
 * Everything else, under ONE protected layout route (W1).
 *
 * `ProtectedRoute` wraps `AppShell` rather than the reverse, so an unauthenticated
 * deep link redirects without painting a shell — no flash of chrome the operator
 * is not entitled to.
 */
export const privateRoutes: RouteObject[] = [
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          {
            index: true,
            path: PRIVATE_ROUTES.DASHBOARD,
            element: withSuspense(<DashboardRoute />),
          },

          /*
           * `PermissionRoute` is a LAYOUT route rendering an `<Outlet />`, so a
           * gated page is a child of it rather than a wrapped element. That is
           * what keeps the gate cheap: the lazy chunk behind it is only fetched
           * once the outlet actually renders, so an operator who may not see the
           * roles editor never downloads it either.
           */
          {
            element: <PermissionRoute anyOf={['members.read']} />,
            children: [
              { path: PRIVATE_ROUTES.MEMBERS, element: withSuspense(<MembersRoute />) },
              { path: PRIVATE_ROUTES.MEMBER_DETAIL, element: withSuspense(<MemberDetailRoute />) },
            ],
          },
          {
            element: <PermissionRoute anyOf={['access.read', 'members.read']} />,
            children: [
              { path: PRIVATE_ROUTES.ADMINS, element: withSuspense(<AdminsOverviewRoute />) },
            ],
          },
          {
            element: <PermissionRoute anyOf={['members.read']} />,
            children: [
              { path: PRIVATE_ROUTES.ADMINS_STAFF, element: withSuspense(<StaffUsersRoute />) },
              {
                path: PRIVATE_ROUTES.ADMINS_STAFF_DETAIL,
                element: withSuspense(<StaffUserDetailRoute />),
              },
            ],
          },
          {
            element: <PermissionRoute anyOf={['access.read']} />,
            children: [
              { path: PRIVATE_ROUTES.ADMINS_ROLES, element: withSuspense(<RolesRoute />) },
            ],
          },
          /*
           * `trust.review` opens both, and the policy editor's SAVE is gated
           * separately on `settings.manage` inside the screen — the value is
           * written through `PUT /admin/settings/:key`, so that is the
           * permission the api will actually check. Gating the route on the
           * write permission instead would hide the policy from the reviewers
           * who work the queue and need to know what it is.
           */
          {
            element: <PermissionRoute anyOf={['trust.review']} />,
            children: [
              { path: PRIVATE_ROUTES.TRUST_POLICY, element: withSuspense(<TrustPolicyRoute />) },
              { path: PRIVATE_ROUTES.TRUST, element: withSuspense(<TrustQueueRoute />) },
            ],
          },
          /*
           * `verification.review` opens both, and it is the only gate either
           * needs: it reads, claims, decides, and judges a step. The one control
           * with a different permission is **waive** (`verification.manage`),
           * and it is gated inside the case screen rather than here — a control's
           * home is decided by its own permission, not by the screen it sits on
           * (P10.6j), and gating the route on the wider key would hide the whole
           * queue from the reviewers it exists for.
           *
           * The detail is listed first, for the reason the file's own header
           * gives about prefixes.
           */
          {
            element: <PermissionRoute anyOf={['verification.review']} />,
            children: [
              /*
               * Before the param route, or `:id` swallows `interviews` and the
               * calendar becomes a case lookup that always fails — the
               * `VENUE_NEW` argument, one route family along.
               */
              {
                path: PRIVATE_ROUTES.VERIFICATION_INTERVIEWS,
                element: withSuspense(<InterviewAvailabilityRoute />),
              },
              {
                path: PRIVATE_ROUTES.VERIFICATION_CASE,
                element: withSuspense(<VerificationCaseRoute />),
              },
              {
                path: PRIVATE_ROUTES.VERIFICATION,
                element: withSuspense(<VerificationQueueRoute />),
              },
            ],
          },
          /*
           * `moderation.review` opens the queue, and it is the only gate the
           * route needs: reading a report — including the reporter's own words,
           * which no table cell can hold — is exactly what that key is for. The
           * VERDICT needs `moderation.manage` and is gated inside the dialog
           * rather than here, because a control's home is decided by its own
           * permission (P10.6j); gating the route on the wider key would hide
           * the queue from the reviewers who work it.
           */
          {
            element: <PermissionRoute anyOf={['moderation.review']} />,
            children: [
              { path: PRIVATE_ROUTES.MODERATION, element: withSuspense(<ModerationQueueRoute />) },
            ],
          },
          /*
           * TWO gates, and they are genuinely different keys.
           *
           * The QUEUE opens on `violations.review`: reading a case — including
           * the message body, which is the evidence and exists nowhere else in
           * the product (N13) — is exactly what that key is for, and the seeded
           * `moderator` role holds it. The consequence needs
           * `violations.manage` and is gated inside the dialog, because a
           * control's home is decided by its own permission (P10.6j).
           *
           * The CATALOGUE answers to `violations.manage` even to read, and that
           * is the api's own choice, not a client one: `classifierGuidance` IS
           * the prompt handed to the model, so reading it is as sensitive as
           * writing it. All four endpoints are gated the same way, so a
           * `review`-only operator sent here would meet a wall — hence the
           * separate route rather than a section of the queue.
           *
           * The child path is listed first, for the reason the file's own header
           * gives about prefixes.
           */
          {
            element: <PermissionRoute anyOf={['violations.manage']} />,
            children: [
              {
                path: PRIVATE_ROUTES.VIOLATION_TYPES,
                element: withSuspense(<ViolationTypesRoute />),
              },
            ],
          },

          /*
           * The whole module answers to `notifications.read`. The writes inside
           * it are gated separately — `notifications.manage` for copy and
           * retries, `settings.manage` for the defaults grid, which is what
           * `PUT /admin/settings/:key` actually checks — so a support role can
           * read what members were told without being able to change what
           * anybody is told next.
           *
           * `new` before `:id`: react-router ranks a static segment above a
           * dynamic one, so the two coexist whichever order they are registered
           * in, and they are listed this way so the next reader need not know
           * that rule.
           */
          {
            element: <PermissionRoute anyOf={['notifications.read']} />,
            children: [
              {
                path: PRIVATE_ROUTES.NOTIFICATION_TEMPLATE_NEW,
                element: withSuspense(<NotificationTemplateEditorRoute />),
              },
              {
                path: PRIVATE_ROUTES.NOTIFICATION_TEMPLATE_DETAIL,
                element: withSuspense(<NotificationTemplateEditorRoute />),
              },
              {
                path: PRIVATE_ROUTES.NOTIFICATION_TEMPLATES,
                element: withSuspense(<NotificationTemplatesRoute />),
              },
              {
                path: PRIVATE_ROUTES.NOTIFICATION_DELIVERIES,
                element: withSuspense(<NotificationDeliveriesRoute />),
              },
              {
                // `new` before `:id`, as the template editor's pair is listed —
                // react-router ranks a static segment above a dynamic one either
                // way, and the order says so without the reader knowing that.
                path: PRIVATE_ROUTES.NOTIFICATION_CAMPAIGN_NEW,
                element: withSuspense(<NotificationCampaignComposerRoute />),
              },
              {
                path: PRIVATE_ROUTES.NOTIFICATION_CAMPAIGN_DETAIL,
                element: withSuspense(<NotificationCampaignComposerRoute />),
              },
              {
                path: PRIVATE_ROUTES.NOTIFICATION_CAMPAIGNS,
                element: withSuspense(<NotificationCampaignsRoute />),
              },
              {
                path: PRIVATE_ROUTES.NOTIFICATION_DEFAULTS,
                element: withSuspense(<NotificationDefaultsRoute />),
              },
            ],
          },
          {
            element: <PermissionRoute anyOf={['violations.review']} />,
            children: [
              { path: PRIVATE_ROUTES.VIOLATIONS, element: withSuspense(<ViolationQueueRoute />) },
            ],
          },
          {
            element: <PermissionRoute anyOf={['settings.read']} />,
            children: [
              { path: PRIVATE_ROUTES.SETTINGS, element: withSuspense(<SettingsRoute />) },
              { path: PRIVATE_ROUTES.SETTINGS_GROUP, element: withSuspense(<SettingsRoute />) },
            ],
          },
          {
            element: <PermissionRoute anyOf={['integrations.read']} />,
            children: [
              { path: PRIVATE_ROUTES.INTEGRATIONS, element: withSuspense(<IntegrationsRoute />) },
            ],
          },
          /*
           * Creating needs `questions.manage`; opening one needs only
           * `questions.read`, and the editor renders itself read-only for an
           * operator who cannot write. Two gates rather than one, because
           * "there is nothing here you may do" is a worse answer than not
           * offering the route at all.
           */
          {
            element: <PermissionRoute anyOf={['questions.manage']} />,
            children: [
              { path: PRIVATE_ROUTES.QUESTION_NEW, element: withSuspense(<QuestionEditorRoute />) },
            ],
          },
          {
            element: <PermissionRoute anyOf={['questions.read']} />,
            children: [
              { path: PRIVATE_ROUTES.QUESTIONS, element: withSuspense(<QuestionsRoute />) },
              {
                path: PRIVATE_ROUTES.QUESTION_DETAIL,
                element: withSuspense(<QuestionEditorRoute />),
              },
            ],
          },
          /*
           * Legal documents (P7b), split on Questions' terms: drafting needs
           * `legal.manage`, reading a document, a version or its acceptances
           * needs `legal.read`, and the editor renders itself read-only for an
           * operator who cannot write.
           */
          {
            element: <PermissionRoute anyOf={['legal.manage']} />,
            children: [
              {
                path: PRIVATE_ROUTES.LEGAL_VERSION_NEW,
                element: withSuspense(<LegalVersionEditorRoute />),
              },
            ],
          },
          {
            element: <PermissionRoute anyOf={['legal.read']} />,
            children: [
              { path: PRIVATE_ROUTES.LEGAL, element: withSuspense(<LegalRoute />) },
              {
                path: PRIVATE_ROUTES.LEGAL_VERSION_DETAIL,
                element: withSuspense(<LegalVersionEditorRoute />),
              },
              {
                path: PRIVATE_ROUTES.LEGAL_VERSION_CONSENTS,
                element: withSuspense(<LegalVersionConsentsRoute />),
              },
              {
                path: PRIVATE_ROUTES.LEGAL_DOCUMENT_DETAIL,
                element: withSuspense(<LegalDocumentDetailRoute />),
              },
            ],
          },
          /*
           * The curriculum, gated exactly as Questions is and for the same
           * reason: authoring a course needs `courses.manage`, reading one needs
           * only `courses.read`, and the editor renders itself read-only for an
           * operator who cannot write. The split is the point of P22's two
           * permissions — support work answers "why has this member not got
           * their discount?" without the authority to author what grants money.
           */
          /*
           * The venue directory (P25a1). Same split as the curriculum above and
           * for the same reason: `meetings.read` is what a support operator
           * needs to answer "where is this pair meeting on Thursday?", while
           * `meetings.manage` authors the places members are actually sent to.
           * The editor is READABLE by anyone who can open the route; every write
           * inside it is gated on its own, and the page drops the action row.
           */
          {
            element: <PermissionRoute anyOf={['meetings.manage']} />,
            children: [
              { path: PRIVATE_ROUTES.VENUE_NEW, element: withSuspense(<VenueDetailRoute />) },
            ],
          },
          {
            element: <PermissionRoute anyOf={['meetings.read']} />,
            children: [
              /*
               * Booking oversight (P25d2), under the SAME gate as the venues it
               * is arranged against: reading who is meeting where is exactly the
               * question `meetings.read` exists for. Both writes inside it —
               * the outcome and the staff cancel — are `meetings.manage`, gated
               * on the controls themselves, so a reader gets the whole surface
               * and no action that would 403.
               */
              { path: PRIVATE_ROUTES.MEETINGS, element: withSuspense(<MeetingsRoute />) },
              {
                path: PRIVATE_ROUTES.MEETING_BOOKING_DETAIL,
                element: withSuspense(<BookingDetailRoute />),
              },
              { path: PRIVATE_ROUTES.VENUES, element: withSuspense(<VenuesRoute />) },
              { path: PRIVATE_ROUTES.VENUE_DETAIL, element: withSuspense(<VenueDetailRoute />) },
              {
                path: PRIVATE_ROUTES.VENUE_AVAILABILITY,
                element: withSuspense(<VenueAvailabilityRoute />),
              },
            ],
          },
          {
            element: <PermissionRoute anyOf={['courses.manage']} />,
            children: [
              { path: PRIVATE_ROUTES.COURSE_NEW, element: withSuspense(<CourseDetailRoute />) },
              { path: PRIVATE_ROUTES.TEST_NEW, element: withSuspense(<TestDetailRoute />) },
              {
                path: PRIVATE_ROUTES.DISCOUNT_RULE_NEW,
                element: withSuspense(<DiscountRuleDetailRoute />),
              },
            ],
          },
          {
            element: <PermissionRoute anyOf={['courses.read']} />,
            children: [
              { path: PRIVATE_ROUTES.COURSES, element: withSuspense(<CoursesRoute />) },
              { path: PRIVATE_ROUTES.COURSE_DETAIL, element: withSuspense(<CourseDetailRoute />) },
              { path: PRIVATE_ROUTES.TESTS, element: withSuspense(<TestsRoute />) },
              { path: PRIVATE_ROUTES.TEST_DETAIL, element: withSuspense(<TestDetailRoute />) },
              {
                path: PRIVATE_ROUTES.DISCOUNT_RULES,
                element: withSuspense(<DiscountRulesRoute />),
              },
              {
                path: PRIVATE_ROUTES.DISCOUNT_RULE_DETAIL,
                element: withSuspense(<DiscountRuleDetailRoute />),
              },
              // `courses.read` and not `courses.manage`: the revoke and the
              // reset inside are gated on their own, and this screen is the
              // reason the read key exists — support work that must not require
              // the authority to author what grants money.
              {
                path: PRIVATE_ROUTES.MEMBER_DISCOUNTS,
                element: withSuspense(<MemberDiscountsRoute />),
              },
            ],
          },
          /*
           * The subscription catalogue, split the way Courses is: authoring a
           * plan needs `billing.manage`, reading one needs only `billing.read`,
           * and the editor renders itself read-only for an operator who cannot
           * write.
           *
           * The PRICING screen is under `billing.read` too, deliberately — the
           * prices are a **setting**, so what actually gates them is
           * `settings.read`, which is not a billing key at all. Gating the route
           * on it would refuse the whole page to an operator who came to read
           * the plan mode; the card inside reports `restricted` for itself, in
           * words, which is the state a real permission split deserves (P15a).
           */
          {
            element: <PermissionRoute anyOf={['billing.manage']} />,
            children: [
              {
                path: PRIVATE_ROUTES.SUBSCRIPTION_NEW,
                element: withSuspense(<SubscriptionDetailRoute />),
              },
            ],
          },
          {
            element: <PermissionRoute anyOf={['billing.read']} />,
            children: [
              { path: PRIVATE_ROUTES.SUBSCRIPTIONS, element: withSuspense(<SubscriptionsRoute />) },
              {
                path: PRIVATE_ROUTES.SUBSCRIPTION_PRICING,
                element: withSuspense(<SubscriptionPricingRoute />),
              },
              // `billing.read` and not `billing.manage`, deliberately: the list,
              // the filters and the mirroring notice are worth reading without
              // it, and the four writes are gated on their own CONTROLS (N22).
              // Listed before the `:id` route for the next reader — react-router
              // ranks a static segment above a dynamic one either way.
              {
                path: PRIVATE_ROUTES.OFFER_CODES,
                element: withSuspense(<OfferCodesRoute />),
              },
              {
                path: PRIVATE_ROUTES.SUBSCRIPTION_DETAIL,
                element: withSuspense(<SubscriptionDetailRoute />),
              },
            ],
          },
          /*
           * The direct-payment channel (P23b2). Entering an order is a WRITE, so
           * it sits behind `billing.manage`; the queue and one order's record
           * are reads, and an operator holding `billing.read` alone gets the
           * whole record with none of the five decisions — the page draws no
           * buttons rather than offering ones the api would refuse (N22).
           */
          {
            element: <PermissionRoute anyOf={['billing.manage']} />,
            children: [
              { path: PRIVATE_ROUTES.PAYMENT_NEW, element: withSuspense(<PaymentEntryRoute />) },
            ],
          },
          {
            element: <PermissionRoute anyOf={['billing.read']} />,
            children: [
              { path: PRIVATE_ROUTES.PAYMENTS, element: withSuspense(<PaymentsRoute />) },
              // Listed before the `:id` route. React-router ranks a static
              // segment above a dynamic one whichever order they appear in, so
              // this is for the next reader rather than for the router — the
              // convention `QUESTION_NEW` already sets in `route.constant.ts`.
              {
                path: PRIVATE_ROUTES.BILLING_EVENTS,
                element: withSuspense(<BillingEventsRoute />),
              },
              // `billing.read` and not `billing.manage`, deliberately: the whole
              // point of the screen is the diagnosis, which is the support
              // desk's own key. The two writes on it are gated on the CONTROL
              // instead, so a reader gets the record and no buttons (N22).
              {
                path: PRIVATE_ROUTES.ENTITLEMENTS,
                element: withSuspense(<EntitlementsRoute />),
              },
              // Read-only by construction: the ledger has no mutation endpoint,
              // so `billing.read` is the whole of its permission story (P31b).
              {
                path: PRIVATE_ROUTES.LEDGER,
                element: withSuspense(<LedgerRoute />),
              },
              {
                path: PRIVATE_ROUTES.PAYMENT_DETAIL,
                element: withSuspense(<PaymentDetailRoute />),
              },
            ],
          },
          ...devRoutes,
        ],
      },
    ],
  },
];
